import { useQuery } from "@tanstack/react-query";

// Mirrors the payload of GET /api/dashboard/overview (server/dashboard-routes.ts).
export interface OverviewStatCards {
  activeDeals: { total: number; stalled: number; progressing: number };
  buyersInMarket: { total: number; liveProcesses: number };
  ioisReceived: { iois: number; cims: number };
}

export type DealStatusKind = "active" | "quiet" | "stalled" | "no_milestones" | "override";

export interface OverviewDeal {
  projectId: string | null;
  projectName: string | null;
  client: { id: string; name: string };
  lead: { name: string | null } | null;
  keyContact: { name: string } | null;
  stage: string | null;
  engagementType: string | null;
  daysLive: number | null;
  nextMilestone: { name: string; dueInDays: number | null; overdue: boolean } | null;
  buyersCount: number;
  funnel: { teasers: number; ndas: number; cims: number; iois: number; lois: number };
  milestonesDone: number;
  milestonesTotal: number;
  statusKind: DealStatusKind;
}

export type AttnKind = "deadline" | "hygiene";

export interface OverviewAttn {
  kind: AttnKind;
  severity: "high" | "medium" | "low";
  title: string;
  detail: string;
  entity: { type: string; id: string };
  action?: { setEngagementType?: string };
}

export interface OverviewFunnel {
  projectId: string | null;
  projectName: string | null;
  client: { id: string; name: string };
  teasers: number;
  ndas: number;
  cims: number;
  iois: number;
  lois: number;
  conversions: { teaserToNda: number; cimToIoi: number };
}

export interface DashboardOverview {
  statCards: OverviewStatCards;
  activeDeals: OverviewDeal[];
  needsAttention: OverviewAttn[];
  funnels: OverviewFunnel[];
}

export function useDashboardOverview() {
  // The global QueryClient default is staleTime:Infinity + no refocus/interval
  // refetch, which would cache the dashboard forever. Override so it stays live
  // with the CRM: fresh on navigation, on tab refocus, and every 60s while open.
  return useQuery<DashboardOverview>({
    queryKey: ["/api/dashboard/overview"],
    staleTime: 15_000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}
